/**
 * Zod validation schemas for Capaciteitsplanning per dagdeel (Epic C-1 —
 * Fundament, Story C-1.1).
 *
 * Deze schemas valideren ALLEEN shape en eenvoudige per-veld-regels. Story
 * C-1.2 breidt dit bestand uit met `createPlanningTaskSchema`/
 * `updatePlanningTaskSchema` (taken-CRUD), die `qualificationTripleShape` +
 * `isQualificationTripleComplete` hieronder hergebruiken (AD-14) — zelfde
 * "shared refine helper"-gedachte als `packages/shared/src/schemas/coreTasks.ts`'s
 * `.refine()`-patroon (`updateCoreTaskSchema`).
 */
import { z } from 'zod';
import { PLANNING_TASK_COLORS, activityUsesTargetMarket, } from '../types/planning.js';
export const PLANNING_TASK_ACTIVITIES = [
    'vastleggen',
    'controle',
    'hercontrole',
    'valideren_vastleggen',
    'valideren_controle',
    'valideren_hercontrole',
];
/**
 * Alleen kleuren uit het curated `PLANNING_TASK_COLORS`-palet zijn toegestaan
 * (AD-10, server-side afgedwongen). Geen kleur (`null`/`undefined`) is
 * toegestaan — de UI valt dan terug op een neutrale default-tint (C-1.3).
 */
const PLANNING_TASK_COLOR_HEXES = PLANNING_TASK_COLORS.map((c) => c.hex);
export const planningTaskColorSchema = z.enum(PLANNING_TASK_COLOR_HEXES).nullable().optional();
/**
 * De velden van de "vereiste kwalificatie op een taak" (AD-14): een complete
 * eis, of helemaal geen eis — nooit gedeeltelijk. Wat "compleet" betekent
 * hangt sinds 2026-08-03 af van de activiteit; zie
 * `isQualificationTripleComplete`.
 */
export const qualificationTripleShape = {
    requiredActivity: z.enum(PLANNING_TASK_ACTIVITIES).nullable().optional(),
    requiredProductCategoryId: z.number().int().positive().nullable().optional(),
    requiredTargetMarketId: z.number().int().positive().nullable().optional(),
};
/**
 * De gedeelde predicate achter de triple-refine. Geëxporteerd zodat Story
 * C-1.2's `createPlanningTaskSchema`/`updatePlanningTaskSchema` deze exact
 * kunnen hergebruiken in hun eigen `.refine()` (zod's `.refine()` kan niet
 * ge-`.extend()`d worden, dus de predicate — niet het `ZodEffects`-schema
 * hieronder — is het herbruikbare bouwblok).
 *
 * Tot 2026-08-03 was de regel "alle drie gevuld, of alle drie leeg". Dat kan
 * niet meer: bij een activiteit zonder doelmarkt is een gevulde doelmarkt
 * juist fout, en een lege doelmarkt is daar geen halve invulling maar de
 * complete vorm. De regel is dus: geen eis, óf activiteit + categorie plus
 * precies de doelmarkt die bij die activiteit hoort.
 */
export function isQualificationTripleComplete(data) {
    const activity = data.requiredActivity ?? null;
    const category = data.requiredProductCategoryId ?? null;
    const market = data.requiredTargetMarketId ?? null;
    // Helemaal geen eis.
    if (activity === null && category === null && market === null)
        return true;
    // Een eis zonder activiteit of zonder categorie is per definitie half.
    if (activity === null || category === null)
        return false;
    // De activiteit bepaalt of de doelmarkt er hóórt te staan — beide kanten op.
    return activityUsesTargetMarket(activity) ? market !== null : market === null;
}
/**
 * Foutmelding + issue-`path` voor de triple-refine — vertaalbaar naar
 * `400 QUALIFICATION_TRIPLE_INCOMPLETE` op route-niveau (Story C-1.2/C-2.3).
 */
export const QUALIFICATION_TRIPLE_INCOMPLETE_MESSAGE = 'Vereiste kwalificatie: vul activiteit en productcategorie samen in, of laat ze allebei leeg. De doelmarkt hoort erbij bij vastleggen en valideren vastleggen, en blijft leeg bij (her)controle.';
/**
 * Foutmeldingen voor de doelmarkt-regel op een kwalificatie zélf
 * (grant/revoke) — los van `QUALIFICATION_TRIPLE_INCOMPLETE`, dat over de eis
 * op een taak gaat.
 */
export const QUALIFICATION_MARKET_REQUIRED_MESSAGE = 'Deze activiteit werkt per doelmarkt; kies er een.';
export const QUALIFICATION_MARKET_NOT_APPLICABLE_MESSAGE = 'Deze activiteit kent geen doelmarkt; stuur het veld niet mee of zet het op null.';
/**
 * De doelmarkt-regel als zod-controle, gedeeld door het grant-body-schema en
 * het revoke-querystring-schema. Eén implementatie, zodat aan- en uitzetten
 * nooit uit elkaar kunnen lopen.
 */
export function refineQualificationMarket(data, ctx) {
    const heeftMarkt = data.targetMarketId !== null && data.targetMarketId !== undefined;
    const hoortMarkt = activityUsesTargetMarket(data.activity);
    // De positiviteitseis staat hier en niet op het veldtype: zou `.positive()`
    // op het basisschema staan, dan faalt dat vóór deze refine draait en krijgt
    // de client `VALIDATION_ERROR` op `targetMarketId: 0` — terwijl het echte
    // probleem kan zijn dat de activiteit helemaal geen doelmarkt kent. De
    // specifieke regel hoort te winnen van de generieke.
    if (heeftMarkt &&
        hoortMarkt &&
        !(Number.isInteger(data.targetMarketId) && data.targetMarketId > 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['targetMarketId'],
            message: 'Doelmarkt moet een geldig id zijn.',
        });
    }
    if (hoortMarkt && !heeftMarkt) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['targetMarketId'],
            message: QUALIFICATION_MARKET_REQUIRED_MESSAGE,
        });
    }
    if (!hoortMarkt && heeftMarkt) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['targetMarketId'],
            message: QUALIFICATION_MARKET_NOT_APPLICABLE_MESSAGE,
        });
    }
}
/**
 * Standalone, direct-testbare versie van de triple-refine (Story C-1.1,
 * AC 3 / test-guidance 2). Story C-1.2 gebruikt niet dit schema zelf, maar
 * `qualificationTripleShape` + `isQualificationTripleComplete` binnen zijn
 * eigen, grotere `createPlanningTaskSchema`/`updatePlanningTaskSchema`.
 */
export const qualificationTripleSchema = z
    .object(qualificationTripleShape)
    .refine(isQualificationTripleComplete, {
    message: QUALIFICATION_TRIPLE_INCOMPLETE_MESSAGE,
    path: ['requiredActivity'],
});
/**
 * Story C-1.2 — Taken-CRUD (service + routes)
 *
 * `createPlanningTaskSchema`/`updatePlanningTaskSchema` hergebruiken
 * `qualificationTripleShape` + `isQualificationTripleComplete` hierboven
 * (AD-14) in plaats van `qualificationTripleSchema` zelf, omdat een
 * `.refine()`-schema niet ge-`.extend()`d kan worden.
 */
export const createPlanningTaskSchema = z
    .object({
    name: z.string().trim().min(1).max(255),
    description: z.string().trim().max(5000).nullable().optional(),
    color: planningTaskColorSchema,
    ...qualificationTripleShape,
})
    .refine(isQualificationTripleComplete, {
    message: QUALIFICATION_TRIPLE_INCOMPLETE_MESSAGE,
    path: ['requiredActivity'],
});
/**
 * Elk veld is optioneel (partial update, AD-10) — `skills`/`competencyIds`-
 * achtige "vervangt de hele lijst"-semantiek is hier niet van toepassing
 * (planning_tasks heeft geen kind-tabellen). De kwalificatie-triple blijft
 * wel atomair: als één van de drie velden meegestuurd wordt, moeten alle
 * drie meegestuurd worden (of allemaal weggelaten om de bestaande triple
 * ongewijzigd te laten — beide tellen als "leeg" voor de refine).
 */
export const updatePlanningTaskSchema = z
    .object({
    name: z.string().trim().min(1).max(255).optional(),
    description: z.string().trim().max(5000).nullable().optional(),
    color: planningTaskColorSchema,
    ...qualificationTripleShape,
})
    .refine(isQualificationTripleComplete, {
    message: QUALIFICATION_TRIPLE_INCOMPLETE_MESSAGE,
    path: ['requiredActivity'],
});
/**
 * Story C-2.1 — Beheerbare lijsten (productcategorieën & doelmarkten)
 *
 * Beide lijsten delen exact dezelfde vorm (`id`, `name`, `is_archived` —
 * AD-14) en dus hetzelfde create/update-schema. Geen `sortOrder`-veld
 * (adversariële review bevinding 8, geratificeerd in AD-14): items worden
 * altijd alfabetisch op `name` opgehaald.
 */
export const createQualificationListItemSchema = z.object({
    name: z.string().trim().min(1).max(255),
});
export const updateQualificationListItemSchema = z.object({
    name: z.string().trim().min(1).max(255),
});
/**
 * Story C-2.2 — Kwalificatie-matrix (grant/revoke + "wie kan wat")
 *
 * `grantQualificationSchema` is het POST-body-schema (aan-zetten, AD-14: een
 * insert). De velden zijn hier gewone JSON-getallen/-enum-waarde (geen
 * `z.coerce`, dit komt uit `req.body`, niet uit een querystring).
 *
 * `targetMarketId` is sinds 2026-08-03 optioneel: of hij verplicht is, hangt
 * van de activiteit af en wordt door `refineQualificationMarket` bepaald —
 * niet door het veldtype.
 */
export const grantQualificationSchema = z
    .object({
    employeeId: z.number().int().positive(),
    activity: z.enum(PLANNING_TASK_ACTIVITIES),
    productCategoryId: z.number().int().positive(),
    targetMarketId: z.number().int().nullable().optional(),
})
    .superRefine(refineQualificationMarket);
/**
 * Een ontbrekende querystring-parameter komt in drie vormen binnen: helemaal
 * afwezig, als lege string (`?targetMarketId=`), of als de letterlijke tekst
 * `undefined`/`null` wanneer een client een waarde blind heeft
 * ge-`String()`-t. Alle drie betekenen hetzelfde. Zonder deze stap maakt
 * `z.coerce.number()` van een lege string een 0 en van `"undefined"` een NaN,
 * waarna `.positive()` een 400 geeft op een verzoek dat gewoon geldig is.
 */
const leegAlsOntbrekend = (value) => value === '' || value === 'undefined' || value === 'null' ? undefined : value;
/**
 * DELETE-querystring-schema (revoke + AD-15 `dryRun`-telling) — identificeert
 * exact dezelfde velden als hierboven, maar dan als querystring-waarden
 * (`z.coerce.number()` voor de ids, `dryRun` als optionele `'true'`-string —
 * adversariële review bevinding 4: de identificerende parameters zijn
 * verplicht, niet alleen `dryRun`). `targetMarketId` is daarop de uitzondering:
 * die hoort er alleen te staan bij een activiteit die een doelmarkt kent.
 */
export const revokeQualificationQuerySchema = z
    .object({
    employeeId: z.coerce.number().int().positive(),
    activity: z.enum(PLANNING_TASK_ACTIVITIES),
    productCategoryId: z.coerce.number().int().positive(),
    // Geen `.positive()` hier — zie `refineQualificationMarket`: die moet als
    // eerste aan bod komen, anders wint de generieke foutcode van de
    // specifieke.
    targetMarketId: z.preprocess(leegAlsOntbrekend, z.coerce.number().int().optional()),
    dryRun: z.enum(['true', 'false']).optional(),
})
    .superRefine(refineQualificationMarket);
/**
 * GET `/api/v1/qualifications/employees/:employeeId`-param + `/:employeeId`
 * zelf wordt route-side als plain int geparsed (zelfde `parseIdParam`-
 * precedent als `routes/planningTasks.ts`) — geen apart zod-schema nodig
 * daarvoor.
 *
 * GET `/api/v1/qualifications` ("wie kan wat", UX-C3): activiteit is
 * verplicht, productcategorie/doelmarkt optioneel (default "alle").
 */
export const qualificationWhoCanQuerySchema = z.object({
    activity: z.enum(PLANNING_TASK_ACTIVITIES),
    productCategoryId: z.coerce.number().int().positive().optional(),
    // Zelfde `leegAlsOntbrekend` als bij revoke: een lege parameter
    // (`?targetMarketId=`) coerced anders naar 0 en geeft 400, terwijl exact
    // dezelfde vorm op de DELETE wél als "afwezig" gelezen wordt. Twee routes
    // die hetzelfde verzoek verschillend beantwoorden is een valstrik.
    targetMarketId: z.preprocess(leegAlsOntbrekend, z.coerce.number().int().positive().optional()),
});
/**
 * `GET /api/v1/qualifications/employees` — scoped medewerkerlijst voor de
 * "Per medewerker"-weergave (UX-C3, zoekveld + afdeling-filter). Niet in de
 * architectuur-routetabel als apart schema genoemd (die tabel focust op de
 * matrix-mutatie-routes), maar noodzakelijke ondersteunende infrastructuur
 * om AC 3's medewerker-picker te kunnen bouwen — HR mag optioneel filteren
 * op afdeling, een team-manager ziet toch alleen het eigen team (server-side
 * scope, AD-16-lijn).
 */
export const qualificationScopedEmployeesQuerySchema = z.object({
    departmentId: z.coerce.number().int().positive().optional(),
    search: z.string().trim().max(255).optional(),
});
/**
 * Story C-3.1 — `GET /api/v1/planning/board` query. Zelfde `van`/`tot`-
 * periode-vorm + 62-dagen-cap als `attendanceCalendarQuerySchema`
 * (`schemas/calendar.ts`) — een eigen kopie i.p.v. cross-import, zelfde
 * "kleine, domein-eigen schema's blijven los" precedent als
 * `qualificationScopedEmployeesQuerySchema` hierboven. `departmentId` is
 * optioneel — alleen HR/admin mag hiermee filteren (AD-16, servicelaag-check).
 */
const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;
export const planningBoardQuerySchema = z
    .object({
    van: z.string().regex(isoDatePattern, 'Ongeldige datum (verwacht YYYY-MM-DD)'),
    tot: z.string().regex(isoDatePattern, 'Ongeldige datum (verwacht YYYY-MM-DD)'),
    departmentId: z.coerce.number().int().positive().optional(),
})
    .refine((data) => data.van <= data.tot, {
    message: '"van" moet voor of gelijk aan "tot" liggen',
    path: ['van'],
})
    .refine((data) => {
    const from = new Date(`${data.van}T00:00:00Z`);
    const to = new Date(`${data.tot}T00:00:00Z`);
    const diffDays = (to.getTime() - from.getTime()) / 86_400_000;
    return diffDays <= 62;
}, { message: 'Periode mag maximaal 62 dagen beslaan', path: ['tot'] });
/**
 * Story C-4.1 — `GET /api/v1/planning/occupancy` query. Zelfde `van`/`tot`/
 * `departmentId`-vorm + 62-dagen-cap als `planningBoardQuerySchema` hierboven
 * (een eigen kopie i.p.v. `.extend()` — `planningBoardQuerySchema` is een
 * `ZodEffects` na `.refine()`, die kan niet ge-`.extend()`d worden, zelfde
 * precedent als `qualificationTripleSchema`'s docstring) — plus `granularity`
 * (`'dag' | 'week'`, default `'week'`, AC 1).
 */
export const planningOccupancyQuerySchema = z
    .object({
    van: z.string().regex(isoDatePattern, 'Ongeldige datum (verwacht YYYY-MM-DD)'),
    tot: z.string().regex(isoDatePattern, 'Ongeldige datum (verwacht YYYY-MM-DD)'),
    departmentId: z.coerce.number().int().positive().optional(),
    granularity: z.enum(['dag', 'week']).optional().default('week'),
})
    .refine((data) => data.van <= data.tot, {
    message: '"van" moet voor of gelijk aan "tot" liggen',
    path: ['van'],
})
    .refine((data) => {
    const from = new Date(`${data.van}T00:00:00Z`);
    const to = new Date(`${data.tot}T00:00:00Z`);
    const diffDays = (to.getTime() - from.getTime()) / 86_400_000;
    return diffDays <= 62;
}, { message: 'Periode mag maximaal 62 dagen beslaan', path: ['tot'] });
/**
 * Story C-4.2 — `GET /api/v1/planning/conflicts` query. Zelfde `van`/`tot`/
 * `departmentId`-vorm als `planningBoardQuerySchema` — geen `granularity`
 * (conflicten zijn een platte lijst, geen dag/week-weergave).
 */
export const planningConflictsQuerySchema = z
    .object({
    van: z.string().regex(isoDatePattern, 'Ongeldige datum (verwacht YYYY-MM-DD)'),
    tot: z.string().regex(isoDatePattern, 'Ongeldige datum (verwacht YYYY-MM-DD)'),
    departmentId: z.coerce.number().int().positive().optional(),
})
    .refine((data) => data.van <= data.tot, {
    message: '"van" moet voor of gelijk aan "tot" liggen',
    path: ['van'],
})
    .refine((data) => {
    const from = new Date(`${data.van}T00:00:00Z`);
    const to = new Date(`${data.tot}T00:00:00Z`);
    const diffDays = (to.getTime() - from.getTime()) / 86_400_000;
    return diffDays <= 62;
}, { message: 'Periode mag maximaal 62 dagen beslaan', path: ['tot'] });
/**
 * Story C-3.2 — `PUT /api/v1/planning/assignments` (upsert) request body.
 * `items` is 1 (los dagdeel) of exact 2 (AD-11 "hele dag"). Het refine
 * hieronder dwingt de "hele dag"-vorm expliciet af: bij 2 items MOET het
 * dezelfde medewerker/datum/taak zijn met precies één `ochtend`- en één
 * `middag`-item — geen twee ochtenden, geen twee verschillende medewerkers.
 */
const planningDayPartSchema = z.enum(['ochtend', 'middag']);
export const upsertAssignmentItemSchema = z.object({
    employeeId: z.number().int().positive(),
    date: z.string().regex(isoDatePattern, 'Ongeldige datum (verwacht YYYY-MM-DD)'),
    dayPart: planningDayPartSchema,
    taskId: z.number().int().positive(),
});
export const upsertAssignmentsSchema = z
    .object({
    items: z.array(upsertAssignmentItemSchema).min(1).max(2),
})
    .refine((data) => {
    if (data.items.length !== 2)
        return true;
    const [a, b] = data.items;
    return (a.employeeId === b.employeeId &&
        a.date === b.date &&
        a.taskId === b.taskId &&
        a.dayPart !== b.dayPart);
}, {
    message: '"Hele dag" moet twee items zijn voor dezelfde medewerker/datum/taak: één ochtend- en één middag-item',
    path: ['items'],
});
/** Story C-3.2 — `DELETE /api/v1/planning/assignments?employeeId&date&dayPart` query. */
export const deleteAssignmentQuerySchema = z.object({
    employeeId: z.coerce.number().int().positive(),
    date: z.string().regex(isoDatePattern, 'Ongeldige datum (verwacht YYYY-MM-DD)'),
    dayPart: planningDayPartSchema,
});
/**
 * Story C-5.1 — `GET /api/v1/planning/assignments/me` query (AD-16 `/me`-
 * paragraaf, adversariële review bevinding 7). BINDEND, geen optionele
 * clamp: een venster groter dan 14 dagen levert `400` op — dit is de
 * server-side afdwinging van de UX-belofte ("deze + volgende week"), niet
 * een privacymaatregel. Eigen kopie i.p.v. `.extend()` op
 * `planningBoardQuerySchema`, zelfde precedent als
 * `planningOccupancyQuerySchema`/`planningConflictsQuerySchema` hierboven
 * (`ZodEffects` na `.refine()` kan niet ge-`.extend()`d worden). Bewust GEEN
 * `departmentId` — dit endpoint is strikt `req.user.id`-gescoped, er is geen
 * scope-parameter om te misbruiken.
 */
export const myAssignmentsQuerySchema = z
    .object({
    van: z.string().regex(isoDatePattern, 'Ongeldige datum (verwacht YYYY-MM-DD)'),
    tot: z.string().regex(isoDatePattern, 'Ongeldige datum (verwacht YYYY-MM-DD)'),
})
    .refine((data) => data.van <= data.tot, {
    message: '"van" moet voor of gelijk aan "tot" liggen',
    path: ['van'],
})
    .refine((data) => {
    const from = new Date(`${data.van}T00:00:00Z`);
    const to = new Date(`${data.tot}T00:00:00Z`);
    const diffDays = (to.getTime() - from.getTime()) / 86_400_000;
    return diffDays <= 14;
}, { message: 'Periode mag maximaal 14 dagen beslaan', path: ['tot'] });
//# sourceMappingURL=planning.js.map